import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
// Import DatePicker for date selection
import DatePicker from "react-datepicker";
import useForm from "../../../../hooks/useForm";
import FilterButton from "../../../../ui/buttons/FilterButton";
import SelectDropdown from "../../../../ui/forms/SelectDropdown";
import { getAgentListing } from "../../../../API/adminApis";

export const filterDefaultsAgentEarning = {
  agent: "", // Will hold the agent ID (user_id)
  start_date: null,
  end_date: null,
};

// Helper to get the display name, prioritizing the top-level 'name' field
const getAgentDisplayName = (agent) => {
  return agent.name || `${agent.user?.first_name || ''} ${agent.user?.last_name || ''}`.trim();
};

// Renamed component to match import in parent
// showAgentFilter: set to false on the Agent Panel's own earnings page, where the
// viewer can only ever see their own data — no agent picker, and no call to the
// admin-only agent-listing API (which a non-admin agent user may not be permitted to hit).
export default function AgentEarningFilter({ prevFilter, setPrevFilter = () => { }, showAgentFilter = true }) {
  const [showFilter, setShowFilter] = useState(false);

  const [filterData, handleFilterData, resetFilterData, forceResetFilterData] = useForm({
    ...filterDefaultsAgentEarning
  });

  const [agents, setAgents] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);

  useEffect(() => {
    if (showAgentFilter) {
      getAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAgents = async () => {
    try {
      const agentsData = await getAgentListing();
      setAgents(agentsData);

      const options = agentsData.map(agent => {
        const displayName = getAgentDisplayName(agent);
        return {
          // Value sent back is the User ID
          value: agent.user?.user_id ? agent.user.user_id.toString() : '',
          // Store the full agent object for the parent component to potentially use
          data: agent,
          // Label for the dropdown menu
          label: displayName
        };
      });
      setAgentOptions(options);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const closeModal = () => {
    setShowFilter(false);
    resetFilterData();
  };

  const clearFilter = () => {
    // On the Agent Panel (showAgentFilter=false) there's no picker to clear — keep
    // the fixed agent id the parent passed in, only clear the dates.
    setPrevFilter({
      ...filterDefaultsAgentEarning,
      agent: showAgentFilter ? "" : prevFilter.agent,
    });
    closeModal();
  };

  const applyFilter = () => {
    // Send all filter fields to the parent
    setPrevFilter({
      agent: filterData.agent,
      start_date: filterData.start_date,
      end_date: filterData.end_date
    });
    closeModal();
  };

  const handleAgentChange = (selectedId) => {
    handleFilterData("agent", selectedId);
  }

  useEffect(() => {
    if (!!showFilter) {
      // Force reset with all existing filter fields
      forceResetFilterData({
        agent: prevFilter.agent,
        start_date: prevFilter.start_date,
        end_date: prevFilter.end_date
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter, prevFilter]);

  return (
    <>
      <FilterButton
        onClick={() => setShowFilter(true)}
        // Check if any filter value is present — agent is always set (and irrelevant
        // to "is a filter active") when the picker itself is hidden.
        active={(showAgentFilter && !!prevFilter?.agent) || !!prevFilter?.start_date || !!prevFilter?.end_date}
      />

      <Modal size="lg" show={showFilter} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3 px-0">
          {/* Agent Selection Filter */}
          <div className="pb-3 mb-1 px-4">
            <div className="row">
              {showAgentFilter && (
                <div className="col-lg-4 col-md-6 mb-3 mb-lg-0">
                  <label className="col-form-label">Agent</label>
                  <div>
                    <SelectDropdown
                      placeholder="Select Agent"
                      options={agentOptions}
                      // Bind value to the agent ID
                      value={filterData?.agent}
                      // Use the handler that only passes the ID
                      onChange={(val) => handleAgentChange(val)}
                    />
                  </div>
                </div>
              )}
              <div className={showAgentFilter ? "col-lg-4 col-md-6 mb-3 mb-lg-0" : "col-lg-6 col-md-6 mb-3 mb-lg-0"}>
                <label className="col-form-label">Start Date</label>
                <div>
                  <DatePicker
                    selected={filterData.start_date}
                    onChange={(date) => handleFilterData("start_date", date)}
                    className="form-control"
                    placeholderText="Select start date"
                    dateFormat="yyyy-MM-dd"
                    selectsStart
                    startDate={filterData.start_date}
                    endDate={filterData.end_date}
                  />
                </div>
              </div>
              <div className={showAgentFilter ? "col-lg-4 col-md-6" : "col-lg-6 col-md-6"}>
                <label className="col-form-label">End Date</label>
                <div>
                  <DatePicker
                    selected={filterData.end_date}
                    onChange={(date) => handleFilterData("end_date", date)}
                    className="form-control"
                    placeholderText="Select end date"
                    dateFormat="yyyy-MM-dd"
                    selectsEnd
                    startDate={filterData.start_date}
                    endDate={filterData.end_date}
                    s minDate={filterData.start_date}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={clearFilter}>
            Clear Filter
          </Button>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={applyFilter}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}